import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { environment } from '~app/environment';

import { isFirstAuthAtom, logoutAtom, userPhoneAtom } from '~entities/auth';
import { OfferBanner, useSearchOfferByPhone } from '~entities/offer';

import Clickable from '@breeze-platform-ui/clickable';
import { Int16ArrowDown } from '@breeze-platform-ui/iconsPack';
import {
  CaptchaContainer,
  CaptchaProvider,
} from '@breezeventures/web-ph-aws-waf-tools/ui';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';

import { OfferAuth } from '../offer-auth/offer-auth';

export type OfferFormValues = {
  offerPhone: string;
};

export const Offer = ({
  onCancelOffer,
  isLoading,
  onPhoneUpdate,
  onOfferError,
  applicationId,
  phone,
}: {
  applicationId?: string;
  phone?: string | null;
  onCancelOffer: () => void;
  isLoading: boolean;
  onPhoneUpdate: (phone: string) => void;
  onOfferError: () => void;
}) => {
  const logout = useSetAtom(logoutAtom);
  const lastUserPhoneNumber = useSetAtom(userPhoneAtom);

  const setIsFirstAuth = useSetAtom(isFirstAuthAtom);

  const [isOpened, setIsOpened] = useState(true);
  const methods = useForm<OfferFormValues>({
    mode: 'onBlur',
  });

  const {
    data: offer,
    isError,
    isLoading: isOfferLoading,
    isFetching: isOfferFetching,
    refetch,
  } = useSearchOfferByPhone(
    {
      phone,
    },
    { enabled: !!phone }
  );

  useEffect(() => {
    if (isError) {
      onOfferError();
    }
  }, [isError, onOfferError]);

  const isSearchOfferError = isError || offer?.status === 'NOT_FOUND';

  const handleCancelOffer = () => {
    methods.reset({ offerPhone: '' });
    setIsFirstAuth(false);
    lastUserPhoneNumber(null);
    onCancelOffer();

    if (!applicationId) {
      logout();
    }
  };

  const onLogin = (newPhone: string) => {
    if (newPhone === phone) {
      refetch();
      return;
    }
    onPhoneUpdate(newPhone);
  };

  return (
    <div
      className="flex  flex-col items-stretch gap-4"
      data-qa-type="special-offer"
    >
      <Clickable
        style={{
          backgroundColor: 'transparent',
          cursor: 'pointer',
          border: 'none',
        }}
        onClick={() => setIsOpened((opened) => !opened)}
        dataQaType="toggle-special-offer"
      >
        <div className="flex cursor-pointer items-center justify-between">
          <h3 className="text-heading-3 text-text-primary">Special offer</h3>
          <Int16ArrowDown
            theme={{ color: 'var(--tds-color-text-01)' }}
            rotate={isOpened ? 180 : 0}
          />
        </div>
      </Clickable>

      <div
        className={classNames('flex items-stretch gap-5', {
          hidden: !isOpened,
        })}
      >
        <FormProvider {...methods}>
          {offer && offer?.status !== 'NOT_FOUND' ? (
            <OfferBanner
              {...offer.posBannerTemplateParams}
              onCancel={handleCancelOffer}
            />
          ) : (
            <CaptchaProvider captchaApiKey={environment.AWS_WAF_API_KEY}>
              <CaptchaContainer>
                <OfferAuth
                  onLogin={onLogin}
                  isLoading={isLoading || isOfferLoading || isOfferFetching}
                  isSearchOfferError={isSearchOfferError}
                />
              </CaptchaContainer>
            </CaptchaProvider>
          )}
        </FormProvider>
      </div>
      {!isOpened && <div className="p-0 pb-1" />}
    </div>
  );
};
